import { useMemo, useState } from 'react'
import { ChevronDownIcon, TagsIcon } from 'lucide-react'

import { isTextTagCategory, TextTag, TextTagCategory } from '@campai/lib-common/utils/misc'

import { ValueSelect } from '@/components/common/ValueSelect'
import { cn } from '@/components/new/lib/utils'

import { RichTextEditorCopy } from './copy'

type TextTagPickerProps = {
    disabled?: boolean
    copy: RichTextEditorCopy
    tags: Array<TextTag | TextTagCategory>
    onSelect: (tag: string) => void
    panelStyle?: boolean
    compactButton?: boolean
}

type TextTagOption = {
    value: string
    label: string
    displayLabel: string
    description?: string
    sample?: string
    groupLabel?: string
}

export const TextTagPicker = ({
    disabled,
    copy,
    tags,
    onSelect,
    panelStyle = false,
    compactButton = false,
}: TextTagPickerProps) => {
    const [selectedTag, setSelectedTag] = useState<string | null>(null)

    const options = useMemo<TextTagOption[]>(() => {
        const result: TextTagOption[] = []
        let currentGroup = copy.tags.ungrouped

        tags.forEach((tag) => {
            if (isTextTagCategory(tag)) {
                currentGroup = tag.category
                return
            }

            result.push({
                value: tag.tag,
                label: [tag.name || tag.tag, tag.tag, tag.description, tag.value].filter(Boolean).join(' '),
                displayLabel: tag.name || tag.tag,
                description: tag.description,
                sample: tag.value,
                groupLabel: currentGroup,
            })
        })

        return result
    }, [copy.tags.ungrouped, tags])

    return (
        <ValueSelect
            type="selector"
            value={selectedTag}
            onChange={(value) => {
                const nextValue = typeof value === 'string' ? value : Array.isArray(value) ? value[0] : null

                if (!nextValue) {
                    setSelectedTag(null)
                    return
                }

                onSelect(nextValue)
                setSelectedTag(null)
            }}
            options={options}
            placeholder={copy.toolbar.insertTag}
            searchPlaceholder={copy.tags.search}
            emptyContent={copy.tags.empty}
            groupBy={(option) => (option as TextTagOption).groupLabel}
            renderOption={(option) => {
                const textTagOption = option as TextTagOption

                return (
                    <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm">{textTagOption.displayLabel}</span>
                        {textTagOption.description ? (
                            <span className="text-muted-foreground text-xs">{textTagOption.description}</span>
                        ) : null}
                        {textTagOption.sample ? (
                            <span className="text-muted-foreground text-xs">
                                {copy.tags.sample}: {textTagOption.sample}
                            </span>
                        ) : null}
                    </div>
                )
            }}
            buttonProps={{
                variant: 'ghost',
                size: compactButton ? 'xs' : 'sm',
                className: cn(
                    'justify-center text-center shadow-none max-[768px]:m-0 max-[768px]:min-h-11 max-[768px]:w-full',
                    compactButton && 'font-normal',
                    !panelStyle && 'border border-transparent m-1'
                ),
            }}
            contentProps={{ className: 'w-[24rem] max-w-[calc(100vw-2rem)]' }}
            disabled={disabled}
        >
            <span className="inline-flex min-w-0 items-center justify-center gap-1.5">
                <TagsIcon data-icon="inline-start" />
                <span>{copy.toolbar.insertTag}</span>
                <ChevronDownIcon data-icon="inline-end" />
            </span>
        </ValueSelect>
    )
}
